import {
  BG_STYLES,
  Button,
  BUTTON_STATUS,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  CrossIcon,
  LazyImage,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import React, { useContext, useEffect, useState } from 'react'
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  FlatList,
  ScrollView,
} from 'react-native'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import SearchInput from 'app/components/reusable/SearchInput'
import { DIMENSION_HEIGHT } from 'app/utils/common'

import CropCard2 from '../crop-calendar/CropCard2'
import fetchAllCategoriesV2 from '../../apis/fetchAllCropCategories'
import useFarmerProducts from '../../hooks/useFarmerProducts'
import { Product } from '../../models/product'
import ProductCard from './ProductCard'
import useDebounce from '../../hooks/useDebounce'
import postFarmerProducts from '../../apis/postFarmerProducts'
import { FarmerProducts, IdNameImage } from '../../models/farmer'
import Categories from '../../models/crop'

interface AddSowingRecordModalProps {
  isVisible: boolean
  farmerId: string
  crop: IdNameImage
  mappedProducts: FarmerProducts['products']
  onClose: () => void
  onSubmit: () => void
}

let cache = {}

const AddFarmerProductsModal = ({
  isVisible,
  farmerId,
  crop,
  onClose,
  onSubmit,
  mappedProducts,
}: AddSowingRecordModalProps) => {
  const { mediaStyles } = useContext(ResponsiveContext)
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 300)

  const [cropCategories, setCropCategories] = useState<Categories[]>([])
  const [noCategoryExists, setNoCategoryExits] = useState(false)

  const [selectedProducts, setSelectedProducts] = useState<
    Record<string, Product>
  >(() => {
    const map: Record<string, Product> = {}
    mappedProducts.forEach(
      ({ product_id, product_name, image_url }) =>
        (map[product_id] = {
          id: product_id,
          image_url: image_url,
          name: product_name,
        }),
    )
    return map
  })

  const selectedProductsArr = Object.values(selectedProducts)

  const { loading, products } = useFarmerProducts(
    crop.id,
    cropCategories[activeCategoryIndex]?.id.toString(),
    debouncedSearchQuery,
    cache,
  )

  const handleProductClick = (product: Product) => {
    setSelectedProducts(prevState => {
      const newState = { ...prevState }
      if (newState[product.id]) {
        delete newState[product.id]
      } else {
        newState[product.id] = product
      }
      return newState
    })
  }

  const handleSubmit = async () => {
    const selectedProductIds = Object.keys(selectedProducts)
    if (selectedProductIds.length === 0) {
      return
    }
    try {
      await postFarmerProducts(farmerId, crop.id, selectedProductIds)
      onSubmit()
    } catch (error) {
      console.error('Error posting farmer products:', error)
    }
  }

  useEffect(() => {
    setNoCategoryExits(false)
    fetchAllCategoriesV2({ crop_ids: crop.id }).then(response => {
      if (response) {
        setCropCategories(response)
        if (response.length === 0) {
          setNoCategoryExits(true)
        }
      }
    })
    return () => {
      cache = {}
    }
  }, [crop.id])

  return (
    <Modal visible={isVisible} style={COMMON_STYLES.relative}>
      <Pressable
        style={[
          COMMON_STYLES.absolute,
          COMMON_STYLES.right,
          COMMON_STYLES['mt1/2'],
          COMMON_STYLES.mr1,
          COMMON_STYLES.z1,
        ]}
        onPress={onClose}>
        <CrossIcon height={32} width={32} />
      </Pressable>
      {loading && <Spinner type="on-screen" />}
      {isVisible ? (
        <View
          style={[
            COMMON_STYLES.fullHeight,
            mediaStyles({ desktopStyle: COMMON_STYLES.row }),
          ]}>
          <View
            style={[
              COMMON_STYLES.pv2,
              mediaStyles({
                desktopStyle: [styles.selectedCropContainer, styles['ph5/2']],
                mobileStyle: COMMON_STYLES.ph1,
              }),
            ]}>
            <Text
              style={[
                TYPOGRAPHY.HEADING3,
                VARIANTS_STYLES.EMPHASIZED,
                TEXT_STYLES.textNeutral90,
              ]}>
              Select Products used by farmer
            </Text>
            <CropCard2 cropName={crop.name} cropImageUrl={crop.image_url} />

            <FlatList
              numColumns={2}
              data={Object.values(selectedProducts)}
              style={mediaStyles({
                mobileStyle: selectedProductsArr.length
                  ? styles.h190
                  : COMMON_STYLES.displayNone,
              })}
              keyExtractor={item => item.id}
              renderItem={({ item }) => (
                <ProductCard
                  name={item.name}
                  imageUrl={item.image_url}
                  isSelected
                  onCTAClick={() =>
                    setSelectedProducts(prev => {
                      const newState = { ...prev }
                      delete newState[item.id]
                      return newState
                    })
                  }
                  style={{ margin: 4 }}
                />
              )}
            />
          </View>
          <View
            style={[
              mediaStyles({
                desktopStyle: styles.divider,
                mobileStyle: styles.dividerHz,
              }),
            ]}
          />

          <View
            style={[
              COMMON_STYLES.flex1,
              mediaStyles({
                desktopStyle: [styles.selectedCropContainer, COMMON_STYLES.pt2],
                mobileStyle: [COMMON_STYLES.ph1, COMMON_STYLES.pt1],
              }),
            ]}>
            <View
              style={[
                COMMON_STYLES.ph1,
                mediaStyles({ desktopStyle: { marginLeft: 112 } }),
              ]}>
              <SearchInput
                placeholder={'Search products'}
                onChange={(text: string) => setSearchQuery(text)}
                value={searchQuery}
                inputContainerStyle={mediaStyles({
                  desktopStyle: COMMON_STYLES.mt1,
                })}
              />
            </View>

            <View
              style={[
                COMMON_STYLES.flex1,
                COMMON_STYLES.row,
                COMMON_STYLES['mt3/4'],
              ]}>
              {noCategoryExists ? (
                <Text
                  style={[
                    TYPOGRAPHY.TEXT2,
                    VARIANTS_STYLES.MEDIUM,
                    TEXT_STYLES.textNeutral70,
                    COMMON_STYLES.ph2,
                    COMMON_STYLES.pv1,
                  ]}>
                  No products found for this crop
                </Text>
              ) : (
                <FlatList
                  data={cropCategories}
                  style={styles.mw96}
                  showsVerticalScrollIndicator={false}
                  contentContainerStyle={styles.w72}
                  renderItem={({ item, index }) => (
                    <Pressable
                      style={({ pressed }) => [
                        COMMON_STYLES['p1/2'],
                        pressed && COMMON_STYLES['opacity1/2'],
                        index === activeCategoryIndex && BG_STYLES.bgSuccess10,
                        COMMON_STYLES.alignItemsCenter,
                      ]}
                      onPress={() => setActiveCategoryIndex(index)}>
                      <View style={styles.categoryImgContainer}>
                        <LazyImage
                          imageUrl={item.image_url}
                          imgStyles={styles.img32X32}
                          fallBackIcon={
                            <View
                              style={[
                                styles.img32X32,
                                COMMON_STYLES.alignItemsCenter,
                                COMMON_STYLES.justifyCenter,
                              ]}>
                              <Text
                                style={[
                                  TYPOGRAPHY.HEADING1,
                                  VARIANTS_STYLES.EMPHASIZED,
                                  TEXT_STYLES.textNeutral100,
                                ]}>
                                {item.app_display_name[0]}
                              </Text>
                            </View>
                          }
                        />
                      </View>
                      <Text
                        numberOfLines={2}
                        style={[
                          COMMON_STYLES.textCenter,
                          TYPOGRAPHY.CAPTION2,
                          VARIANTS_STYLES.SEMI_EMPHASIZED,
                          index === activeCategoryIndex
                            ? TEXT_STYLES.textPrimary100
                            : TEXT_STYLES.textNeutral70,
                        ]}>
                        {item.app_display_name}
                      </Text>
                      {index === activeCategoryIndex && (
                        <View style={styles.activeIndicator} />
                      )}
                    </Pressable>
                  )}
                />
              )}
              {!noCategoryExists && (
                <View style={COMMON_STYLES.flex1}>
                  <ScrollView style={COMMON_STYLES.flex1}>
                    {loading ? (
                      <Spinner />
                    ) : (
                      <View
                        style={[
                          COMMON_STYLES.flex1,
                          COMMON_STYLES.row,
                          COMMON_STYLES.flexWrap,
                          COMMON_STYLES.ml2,
                          { gap: 16 },
                        ]}>
                        {products.map(({ id, name, image_url }) => (
                          <ProductCard
                            key={id}
                            name={name}
                            imageUrl={image_url}
                            isSelected={selectedProducts[id] !== undefined}
                            onCTAClick={() =>
                              handleProductClick({
                                id,
                                name,
                                image_url,
                              })
                            }
                          />
                        ))}
                        {products.length === 0 && (
                          <Text
                            style={[
                              TYPOGRAPHY.TEXT2,
                              VARIANTS_STYLES.MEDIUM,
                              TEXT_STYLES.textNeutral70,
                              COMMON_STYLES.pv1,
                            ]}>
                            No product found
                          </Text>
                        )}
                      </View>
                    )}
                  </ScrollView>
                  <View
                    style={[
                      COMMON_STYLES.row,
                      COMMON_STYLES.justifyEnd,
                      COMMON_STYLES.fullWidth,
                      COMMON_STYLES.alignItemsEnd,
                      COMMON_STYLES.p1,
                      styles.dividerTop,
                    ]}>
                    <Button
                      onClick={onClose}
                      text={'Back'}
                      type={BUTTON_TYPE.SECONDARY}
                      buttonStyle={mediaStyles({
                        desktopStyle: styles.actionBtn,
                        mobileStyle: COMMON_STYLES.halfWidth,
                      })}
                    />
                    <Button
                      text={'Add & Save'}
                      status={
                        Object.keys(selectedProducts).length > 0
                          ? BUTTON_STATUS.ACTIVE
                          : BUTTON_STATUS.DISABLED
                      }
                      onClick={handleSubmit}
                      buttonStyle={[
                        COMMON_STYLES.ml1,
                        mediaStyles({
                          desktopStyle: styles.actionBtn,
                          mobileStyle: COMMON_STYLES.halfWidth,
                        }),
                      ]}
                    />
                  </View>
                </View>
              )}
            </View>
          </View>
        </View>
      ) : null}
    </Modal>
  )
}

const styles = StyleSheet.create({
  divider: {
    borderRightWidth: 1,
    borderColor: COLORS.neutral20,
    height: DIMENSION_HEIGHT,
  },
  dividerHz: {
    borderBottomWidth: 1,
    borderColor: COLORS.neutral20,
  },
  'ph5/2': {
    paddingHorizontal: 40,
  },

  selectedCropContainer: {
    width: 400,
  },
  actionBtn: {
    width: '25%',
    height: 56,
  },
  img32X32: {
    height: 32,
    width: 32,
  },
  mw96: {
    maxWidth: 96,
  },
  w72: {
    width: 72,
  },
  categoryImgContainer: {
    height: 48,
    width: 48,
    borderRadius: 24,
    backgroundColor: COLORS.neutral10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dividerTop: {
    borderTopWidth: 1,
    borderColor: COLORS.neutral20,
  },
  activeIndicator: {
    position: 'absolute',
    left: 0,
    top: '25%',
    height: '50%',
    width: 2,
    backgroundColor: COLORS.primary100,
  },
  h190: {
    height: 190,
  },
})

export default AddFarmerProductsModal
